import { FC, useMemo } from "react";

import { SessionStatsCard } from "./SessionStatsCard";

import type { ClassSessionType } from "components/server-types";
import { t } from "i18n/i18n";
import { ClassCollaborationIcon, MicrophoneIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import { selectCurrentCourseId, selectStudentClassSessionUserSummaries } from "store/selectors";
import { computeStudentTalkTimeStats } from "utils/metrics-utils";

type StudentTalkTimeCardProps = {
  sessions: ClassSessionType[];
};

export const StudentTalkTimeCard: FC<StudentTalkTimeCardProps> = ({ sessions }) => {
  const courseId = useAppSelector(selectCurrentCourseId);
  const studentSummaries = useAppSelector(selectStudentClassSessionUserSummaries);
  const { canAuthorCourse } = useCapabilities();

  const { averageStudentTalkTime, highestStudentTalkTime } = useMemo(
    () => computeStudentTalkTimeStats(sessions, studentSummaries),
    [sessions, studentSummaries],
  );

  const isEmpty = sessions.length === 0;

  if (!canAuthorCourse || !courseId) return null;

  const highestText = highestStudentTalkTime.date
    ? t("session_log_view.stats.student_talk_time.highest", highestStudentTalkTime)
    : t("session_log_view.stats.student_talk_time.no_sessions");

  return (
    <SessionStatsCard
      FooterIcon={ClassCollaborationIcon}
      footerText={highestText}
      isEmpty={isEmpty}
      kind="student_talk_time"
      title={t("session_log_view.stats.student_talk_time.title")}
      TitleIcon={MicrophoneIcon}
      value={`${averageStudentTalkTime}%`}
      valueDescription={t("session_log_view.stats.student_talk_time.value_description")}
    />
  );
};
